/**
 * useChatConfiguration - Hook for loading chat agents and models
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { auth } from '../../../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useChatConfiguration = () => {
  const [agents, setAgents] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [showAgentMenu, setShowAgentMenu] = useState(false);
  const [showModelMenu, setShowModelMenu] = useState(false);

  // Fetch configuration (agents and models)
  const fetchConfiguration = useCallback(async () => {
    try {
      const token = auth.getToken();
      
      // Fetch agents from correct endpoint
      try {
        const agentsRes = await axios.get(
          `${API}/ai-workspace/agents`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAgents(agentsRes.data.agents || []);
        
        // Set default agent if available
        if (agentsRes.data.agents?.length > 0 && !selectedAgent) {
          const defaultAgent = agentsRes.data.agents.find(a => a.default) || agentsRes.data.agents[0];
          setSelectedAgent(defaultAgent);
        }
      } catch (agentErr) {
        console.log('Agents endpoint not available');
      }

      // Fetch models
      try {
        const modelsRes = await axios.get(
          `${API}/ai/models`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Backend returns models directly as array, not wrapped in {models: []}
        const modelsList = Array.isArray(modelsRes.data) ? modelsRes.data : (modelsRes.data.models || []);
        setModels(modelsList);
        
        // Set default model if available
        if (modelsList.length > 0 && !selectedModel) {
          const defaultModel = modelsList.find(m => m.default) || modelsList[0];
          setSelectedModel(defaultModel);
        }
      } catch (modelErr) {
        console.log('Models endpoint not available');
      }
    } catch (error) {
      console.error('Failed to fetch configuration:', error);
    }
  }, [selectedAgent, selectedModel]);

  // Load configuration on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const token = auth.getToken();
        
        // Fetch agents from correct endpoint
        try {
          const agentsRes = await axios.get(
            `${API}/ai-workspace/agents`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const agentsList = agentsRes.data.agents || [];
          setAgents(agentsList);
          
          // Set default agent if available
          if (agentsList.length > 0) {
            const defaultAgent = agentsList.find(a => a.default) || agentsList[0];
            setSelectedAgent(defaultAgent);
          }
        } catch (agentErr) {
          console.log('Agents endpoint not available, using fallback');
          // Provide fallback agents
          const fallbackAgents = [
            { id: 'lexy-general', name: 'Lexy Generell', description: 'Generell juridisk assistent', default: true },
            { id: 'lexy-contract', name: 'Kontraktsanalytiker', description: 'Spesialist på kontrakter' },
            { id: 'lexy-research', name: 'Juridisk Forsker', description: 'Dyptgående juridisk research' }
          ];
          setAgents(fallbackAgents);
          setSelectedAgent(fallbackAgents[0]);
        }

        // Fetch models
        try {
          const modelsRes = await axios.get(
            `${API}/ai/models`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          // Backend returns models directly as array, not wrapped in {models: []}
          const modelsList = Array.isArray(modelsRes.data) ? modelsRes.data : (modelsRes.data.models || []);
          setModels(modelsList);
          
          // Set default model if available
          if (modelsList.length > 0) {
            const defaultModel = modelsList.find(m => m.default) || modelsList[0];
            setSelectedModel(defaultModel);
          }
        } catch (modelErr) {
          console.log('Models endpoint not available');
          // Provide fallback models so menu works
          const fallbackModels = [
            { id: 'gpt-4o', display_name: 'GPT-4o', name: 'GPT-4o', default: true },
            { id: 'gpt-5', display_name: 'GPT-5', name: 'GPT-5' },
            { id: 'gpt-4o-mini', display_name: 'GPT-4o Mini', name: 'GPT-4o Mini' }
          ];
          setModels(fallbackModels);
          setSelectedModel(fallbackModels[0]);
        }
      } catch (error) {
        console.error('Failed to fetch configuration:', error);
      }
    };
    
    loadConfig();
  }, []);

  // Toggle agent menu
  const toggleAgentMenu = useCallback(() => {
    setShowAgentMenu(prev => !prev);
    setShowModelMenu(false);
  }, []);

  // Toggle model menu
  const toggleModelMenu = useCallback(() => {
    setShowModelMenu(prev => !prev);
    setShowAgentMenu(false);
  }, []);

  // Select an agent
  const selectAgent = useCallback((agent) => {
    setSelectedAgent(agent);
    setShowAgentMenu(false);
  }, []);

  // Select a model
  const selectModel = useCallback((model) => {
    setSelectedModel(model);
    setShowModelMenu(false);
  }, []);

  // Close all menus
  const closeMenus = useCallback(() => {
    setShowAgentMenu(false);
    setShowModelMenu(false);
  }, []);

  return {
    // State
    agents,
    models,
    selectedAgent,
    selectedModel,
    showAgentMenu,
    showModelMenu,
    
    // Actions
    fetchConfiguration,
    toggleAgentMenu,
    toggleModelMenu,
    selectAgent,
    selectModel,
    closeMenus
  };
};

export default useChatConfiguration;
